import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Compass, Mail, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import MagneticButton from "../components/UI/MagneticButton";

/*
  Previous projects showcase
  --------------------------
  The former carousel, project filters, and project-card display have been
  intentionally retired for now. This page is a deliberate holding experience
  until the next collection is ready to be revealed.
*/

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

const ProjectsPage = () => {
  const navigate = useNavigate();

  return (
    <section className="relative isolate min-h-screen overflow-hidden bg-[#070807] px-5 pb-20 pt-32 text-white sm:px-8 md:pt-40">
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute left-1/2 top-[-18rem] h-[42rem] w-[42rem] -translate-x-1/2 rounded-full bg-[#d9f99d]/[0.11] blur-[130px]" />
        <div className="absolute -left-36 bottom-0 h-80 w-80 rounded-full bg-emerald-500/[0.08] blur-[110px]" />
        <div className="absolute -right-36 top-1/3 h-80 w-80 rounded-full bg-lime-300/[0.07] blur-[110px]" />
        <div
          className="absolute inset-0 opacity-[0.16]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(217,249,157,0.11) 1px, transparent 1px), linear-gradient(90deg, rgba(217,249,157,0.11) 1px, transparent 1px)",
            backgroundSize: "56px 56px",
            maskImage: "radial-gradient(ellipse 70% 55% at 50% 45%, black, transparent 78%)",
          }}
        />
      </div>

      <div className="mx-auto flex max-w-6xl flex-col items-center text-center">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.65, ease: "easeOut" }}
          className="inline-flex items-center gap-2 rounded-full border border-[#d9f99d]/25 bg-[#d9f99d]/[0.07] px-4 py-2 font-mono text-[10px] font-bold uppercase tracking-[0.22em] text-[#d9f99d]"
        >
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#d9f99d] opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-[#d9f99d]" />
          </span>
          Projects / coming soon
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7, delay: 0.12, ease: "easeOut" }}
          className="relative mt-8"
        >
          <div className="pointer-events-none absolute -left-7 -top-7 hidden text-[#d9f99d] sm:block">
            <Sparkles size={26} strokeWidth={1.5} />
          </div>
          <h1 className="font-display text-[clamp(3.4rem,11vw,9.5rem)] font-extrabold uppercase leading-[0.78] tracking-[-0.085em]">
            <span className="block">Great work</span>
            <span className="block bg-gradient-to-r from-[#d9f99d] via-white to-[#d9f99d] bg-clip-text text-transparent">
              takes shape.
            </span>
          </h1>
          <div className="pointer-events-none absolute -bottom-7 -right-8 hidden h-16 w-16 rotate-12 rounded-2xl border border-[#d9f99d]/40 sm:block" />
        </motion.div>

        <motion.p
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7, delay: 0.24, ease: "easeOut" }}
          className="mt-9 max-w-xl text-base leading-relaxed text-zinc-400 sm:text-lg"
        >
          Our next chapter of digital experiences is taking form behind the scenes.
          We&apos;re polishing every pixel, interaction, and unexpected little detail
          before we put it in the spotlight.
        </motion.p>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7, delay: 0.34, ease: "easeOut" }}
          className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
        >
          <MagneticButton
            onClick={() => navigate("/contact")}
            className="group flex items-center gap-3 rounded-full bg-[#d9f99d] px-7 py-3.5 text-sm font-bold text-black transition-colors hover:bg-white"
          >
            <span>Start a conversation</span>
            <ArrowUpRight className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" size={18} />
          </MagneticButton>
          <button
            onClick={() => navigate("/services")}
            className="group flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 text-sm font-medium text-zinc-200 transition-colors hover:border-[#d9f99d]/60 hover:text-[#d9f99d]"
          >
            Explore what we do
            <Compass size={17} className="transition-transform duration-300 group-hover:rotate-45" />
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 25 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.75, delay: 0.48, ease: "easeOut" }}
          className="relative mt-16 w-full max-w-4xl overflow-hidden rounded-[2rem] border border-white/10 bg-white/[0.035] p-1 text-left shadow-2xl shadow-black/30"
        >
          <div className="relative overflow-hidden rounded-[1.8rem] border border-white/[0.06] bg-gradient-to-br from-white/[0.07] to-transparent px-6 py-7 sm:px-9 sm:py-8">
            <div className="absolute right-0 top-0 h-32 w-32 -translate-y-1/2 translate-x-1/2 rounded-full bg-[#d9f99d]/10 blur-2xl" />
            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="mb-3 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-[#d9f99d]">
                  <span className="h-px w-6 bg-[#d9f99d]" />
                  Currently crafting
                </div>
                <p className="font-display text-2xl font-bold tracking-tight text-white sm:text-3xl">
                  A new collection is loading.
                </p>
              </div>
              <a
                href="mailto:info@dylora.in"
                className="group flex shrink-0 items-center gap-2 self-start rounded-full border border-white/10 bg-black/20 px-4 py-2.5 font-mono text-[10px] uppercase tracking-wider text-zinc-300 transition-colors hover:border-[#d9f99d]/50 hover:text-[#d9f99d] sm:self-auto"
              >
                <Mail size={14} />
                Work with us
              </a>
            </div>
            <div className="mt-7 flex gap-2">
              {[0, 1, 2, 3, 4, 5, 6, 7].map((item) => (
                <span
                  key={item}
                  className="h-1 flex-1 rounded-full bg-white/10"
                  style={{ opacity: item < 5 ? 1 : 0.45 }}
                >
                  {item < 5 && <span className="block h-full rounded-full bg-[#d9f99d]" />}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProjectsPage;
